import React from 'react';
import { Link } from 'react-router-dom';
import { AlertCircle } from 'lucide-react';

const Disclaimer = () => {
  return (
    <div className="bg-white">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-slate-50 to-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="text-center max-w-3xl mx-auto">
            <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6">Disclaimer</h1>
            <p className="text-sm text-gray-600">Last Updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="bg-yellow-50 border-l-4 border-yellow-500 p-6 rounded mb-8">
          <div className="flex items-start">
            <AlertCircle size={24} className="text-yellow-700 mr-3 mt-1 flex-shrink-0" />
            <p className="text-gray-700">
              <strong>Important:</strong> This website provides general information only. It does not constitute professional advice, a binding offer, or a commitment to provide services.
            </p>
          </div>
        </div>

        <div className="prose prose-lg max-w-none space-y-8">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">1. General Information Only</h2>
            <p className="text-gray-700 leading-relaxed">
              All content on this website is provided for general informational purposes only. It should not be relied upon as:
            </p>
            <ul className="list-disc pl-6 mt-4 space-y-2 text-gray-700">
              <li>Professional, legal, financial, or technical advice</li>
              <li>A comprehensive or current description of services</li>
              <li>An offer, commitment, guarantee, or assurance of any kind</li>
              <li>A binding agreement or contract</li>
            </ul>
          </div>

          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">2. No Binding Commitments</h2>
            <p className="text-gray-700 leading-relaxed">
              Nothing on this website creates a binding obligation, commitment, or agreement between BRX Resource and any visitor or prospective client. All engagements require:
            </p>
            <ul className="list-disc pl-6 mt-4 space-y-2 text-gray-700">
              <li>A separate written agreement executed by authorized representatives</li>
              <li>Documented approvals and written confirmations</li>
              <li>Mutually agreed terms and conditions</li>
              <li>Compliance with applicable statutory and regulatory requirements</li>
            </ul>
          </div>

          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">3. No Guarantees or Warranties</h2>
            <p className="text-gray-700 leading-relaxed">
              BRX Resource makes no representations, warranties, or guarantees regarding:
            </p>
            <ul className="list-disc pl-6 mt-4 space-y-2 text-gray-700">
              <li>Accuracy, completeness, or currency of website information</li>
              <li>Availability, quality, or suitability of services</li>
              <li>Candidate availability, qualifications, or performance</li>
              <li>Timelines, delivery schedules, or project outcomes</li>
              <li>Pricing, rates, or commercial terms</li>
            </ul>
          </div>

          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">4. No Client References</h2>
            <p className="text-gray-700 leading-relaxed">
              This website does not reference specific clients, projects, case studies, or testimonials. Any engagement details, performance metrics, or client information are confidential and not disclosed publicly.
            </p>
          </div>

          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Subject to Change</h2>
            <p className="text-gray-700 leading-relaxed">
              Information on this website may be changed, updated, or removed at any time without notice. BRX Resource is not obligated to update information or notify users of changes.
            </p>
          </div>

          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Vendor-to-Vendor Model</h2>
            <p className="text-gray-700 leading-relaxed">
              BRX Resource operates on a vendor-to-vendor engagement model. Services are provided to staffing firms, MSPs, and enterprise vendors—not directly to end clients. All references to "services" on this website assume a vendor-to-vendor contractual relationship.
            </p>
          </div>

          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">7. No Employment Relationship</h2>
            <p className="text-gray-700 leading-relaxed">
              Nothing on this website creates an employment relationship between BRX Resource and any candidate, contractor, or end user. Candidate placement and contractor management are subject to written agreements and applicable employment laws.
            </p>
          </div>

          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Compliance and Certifications</h2>
            <p className="text-gray-700 leading-relaxed">
              BRX Resource does not claim ISO, SOC2, HIPAA, or other third-party certifications unless explicitly stated otherwise. Compliance measures are aligned to contractual obligations and reasonable administrative controls.
            </p>
          </div>

          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Limitation of Liability</h2>
            <p className="text-gray-700 leading-relaxed">
              To the fullest extent permitted by law, BRX Resource disclaims all liability for any loss, damage, or expense arising from:
            </p>
            <ul className="list-disc pl-6 mt-4 space-y-2 text-gray-700">
              <li>Use of or reliance on website information</li>
              <li>Errors, omissions, or inaccuracies in content</li>
              <li>Decisions made based on website information</li>
              <li>Interruptions, delays, or technical issues</li>
            </ul>
          </div>

          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Independent Verification Required</h2>
            <p className="text-gray-700 leading-relaxed">
              Users are responsible for independently verifying any information obtained from this website before making business decisions or entering into agreements.
            </p>
          </div>

          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">11. Contact for Clarification</h2>
            <p className="text-gray-700 leading-relaxed">
              If you have questions about this disclaimer or require clarification on any matter, please contact us at:
            </p>
            <p className="text-gray-700 leading-relaxed mt-4">
              Email: info@brxresource.com<br />
              Location: Noida, Uttar Pradesh, India
            </p>
          </div>

          <div className="mt-12 pt-8 border-t border-gray-200">
            <Link to="/" className="text-blue-900 hover:text-blue-800 font-medium">
              ← Back to Home
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Disclaimer;